import React, { useCallback, useMemo, useState } from 'react';
import { BookmarkNotePopover } from '@sales-portal/domain/pricing/components/bookmark-note-popover';
import { buildPricingInputValidator } from '@sales-portal/domain/pricing/screens/management/blocs/utils/pricing-input-validator';
import { createStyles } from '@/contexts/theme-context';
import { EMPTY_VALUE_LABEL } from '@sales-portal/constants';
import {
  formatCurrency,
  formatPriceInMicrosWithoutSymbol,
} from '@/utils/money';
import { formatPercentage } from '@sales-portal/blocs/utils/money';
import {
  getQuickActionMetadata,
  PricingTableQuickAction,
} from '@sales-portal/domain/pricing/screens/management/components/pricing-table-quick-actions-popover';
import {
  GrossMarginEditAction,
  PriceEditAction,
  PricingEditField,
  RevertEditAction,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { isPricingAtBaseline } from '@sales-portal/domain/pricing/screens/management/blocs/utils/pricing-edit-normalization';
import {
  OverridePricing,
  recommendationPricing,
} from '@/experiences/sales-portal/domain/pricing-agent/screens/utils/recommendation-pricing';
import { parseTransientAwareNumber } from '@sales-portal/domain/pricing/screens/management/blocs/utils/numeric-input';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
  PricingPageSource,
} from '@/experiences/sales-portal/domain/pricing/analytics/pricing-management-analytics';
import { PricingRecommendationRow } from '@/experiences/sales-portal/domain/pricing-agent/screens/types';
import {
  PricingTableInput,
  PricingTableInputPopoverConfig,
  PricingTableInputPopoverKind,
} from '@sales-portal/domain/pricing/screens/management/components/pricing-table-input';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useBookmarkAction } from '@sales-portal/domain/pricing/blocs/hooks/use-bookmark-action';
import { useInputArrowNavigation } from '@sales-portal/domain/pricing/screens/management/blocs/hooks/use-input-arrow-navigation';
import { usePricingCellFocus } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-cell-focus';
import { usePricingTableUIStore } from '@sales-portal/domain/pricing/blocs/stores/pricing-table-ui-store';
import { useRecommendationActionsStore } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store';
import { View } from 'react-native';

interface Props {
  canBookmarkItem: boolean;
  /** Table column id, for the table's horizontal scroll-to-focus. */
  columnId: string;
  currencyCode: string;
  disabled: boolean;
  inputType: PricingEditField;
  isHistoricalPricingContextEnabled: boolean;
  row: PricingRecommendationRow;
  /** Positional index in the rendered rows, for the table's scroll-to-focus. */
  tableRowIndex: number;
}

export const AgentInputCell = React.memo<Props>(
  ({
    canBookmarkItem,
    columnId,
    currencyCode,
    disabled,
    inputType,
    isHistoricalPricingContextEnabled,
    row,
    tableRowIndex,
  }) => {
    const [styles, theme] = useStyles();
    const isPrice = inputType === PricingEditField.Price;

    const {
      bookmarkAction,
      bookmarkNote,
      isBookmarked,
      isNoteOpen,
      onSaveNote,
    } = useBookmarkAction({
      canBookmarkItem,
      pageSource: PricingPageSource.AGENT_REVIEW,
      restaurantUUID: row.restaurantUUID,
      supplierUUID: row.supplierUUID,
      variantPackUUID: row.variantPackUUID,
    });
    const overridePriceInMicros = useRecommendationActionsStore(
      (state) => state.actions[row.uuid]?.overridePriceInMicros,
    );
    const overrideGrossProfitInPercentage = useRecommendationActionsStore(
      (state) => state.actions[row.uuid]?.overrideGrossProfitInPercentage,
    );
    const acceptRecommendation = useRecommendationActionsStore(
      (state) => state.acceptRecommendation,
    );
    const clearOverride = useRecommendationActionsStore(
      (state) => state.clearOverride,
    );
    const setOverridePrice = useRecommendationActionsStore(
      (state) => state.setOverridePrice,
    );
    // The flag is table-wide, but only the focused cell's popover can show it;
    // gating the selector keeps every other cell out of the re-render.
    const [isFocused, setIsFocused] = useState(false);
    const isQuickActionsOpen = usePricingTableUIStore(
      (state) => isFocused && state.isQuickActionsOpen,
    );
    const setQuickActionsOpen = usePricingTableUIStore(
      (state) => state.setQuickActionsOpen,
    );

    const hasOverride = isPrice
      ? overridePriceInMicros !== undefined
      : overrideGrossProfitInPercentage !== undefined;
    const idPrefix = `agent-${isPrice ? 'price' : 'gm'}-input-`;
    const inputID = `${idPrefix}${row.uuid}`;

    const originalValue = isPrice
      ? formatPriceInMicrosWithoutSymbol(row.newPriceInMicros)
      : row.grossProfitInPercentage.toFixed(1);

    const updatedValue = !hasOverride
      ? null
      : isPrice
        ? formatPriceInMicrosWithoutSymbol(
            overridePriceInMicros ?? row.newPriceInMicros,
          )
        : (
            overrideGrossProfitInPercentage ?? row.grossProfitInPercentage
          ).toFixed(1);

    const applyPricing = useCallback(
      (pricing: OverridePricing | null, quickAction: string | null) => {
        if (!pricing) {
          return;
        }
        const overrideAtBaseline = isPricingAtBaseline({
          baselineGrossMarginPercentage: row.grossProfitInPercentage,
          baselinePriceInMicros: row.newPriceInMicros,
          grossMarginPercentage: pricing.grossProfitInPercentage,
          priceInMicros: pricing.priceInMicros,
          updatedField: inputType,
        });

        if (overrideAtBaseline) {
          acceptRecommendation(
            row.uuid,
            row.restaurantUUID,
            row.variantPackUUID,
            row.supplierUUID,
          );
          return;
        }
        setOverridePrice(
          row.uuid,
          pricing.priceInMicros,
          pricing.grossProfitInPercentage,
          row.supplierUUID,
        );
        pricingManagementAnalytics.track(PricingAnalyticEvent.AGENT_OVERRIDE, {
          inputType,
          quickAction,
          recommendationUUID: row.uuid,
          restaurantUUID: row.restaurantUUID,
          supplierUUID: row.supplierUUID,
          variantPackUUID: row.variantPackUUID,
        });
      },
      [
        acceptRecommendation,
        inputType,
        row.grossProfitInPercentage,
        row.newPriceInMicros,
        row.restaurantUUID,
        row.supplierUUID,
        row.uuid,
        row.variantPackUUID,
        setOverridePrice,
      ],
    );

    const handleBlur = useCallback(
      (nextValue: string) => {
        const parsed = parseTransientAwareNumber(nextValue);
        if (parsed === null) {
          return;
        }
        const pricing = isPrice
          ? recommendationPricing.fromPriceUnits(
              parsed,
              row.snapshotCostInMicros,
            )
          : recommendationPricing.fromGrossProfitPercent(
              parsed,
              row.snapshotCostInMicros,
            );
        applyPricing(pricing, null);
      },
      [applyPricing, isPrice, row.snapshotCostInMicros],
    );

    const validate = useMemo(
      () =>
        buildPricingInputValidator({
          costInMicros: row.snapshotCostInMicros,
          currencyCode,
          inputType,
          marginUpperBound: 100,
        }),
      [currencyCode, inputType, row.snapshotCostInMicros],
    );

    const onArrowNavigation = useInputArrowNavigation(inputID, idPrefix);
    const { isAnchorSettled, onFocus: reportCellFocus } = usePricingCellFocus(
      tableRowIndex,
      columnId,
    );

    const displayedPriceInMicros =
      overridePriceInMicros ?? row.newPriceInMicros;
    const displayedGmPct =
      overrideGrossProfitInPercentage ?? row.grossProfitInPercentage;

    const quickActions = useMemo<PricingTableQuickAction[]>(() => {
      const revertAction: PricingTableQuickAction = {
        ...getQuickActionMetadata(RevertEditAction.RevertEdit),
        disabled: !hasOverride,
        onSelect: () => {
          clearOverride(row.uuid);
          pricingManagementAnalytics.track(
            PricingAnalyticEvent.AGENT_OVERRIDE_REVERSION,
            {
              recommendationUUID: row.uuid,
              restaurantUUID: row.restaurantUUID,
              supplierUUID: row.supplierUUID,
              variantPackUUID: row.variantPackUUID,
            },
          );
        },
        valueLabel: isPrice
          ? formatCurrency(row.newPriceInMicros, currencyCode)
          : formatPercentage(row.grossProfitInPercentage, 1, 1),
      };

      const priceValueLabel = (pricing: OverridePricing | null) =>
        pricing
          ? formatCurrency(pricing.priceInMicros, currencyCode)
          : EMPTY_VALUE_LABEL;
      const gmValueLabel = (pricing: OverridePricing | null) =>
        pricing
          ? formatPercentage(pricing.grossProfitInPercentage, 1, 1)
          : EMPTY_VALUE_LABEL;

      if (isPrice) {
        const endInX9 = recommendationPricing.endInXNine(
          displayedPriceInMicros,
          row.snapshotCostInMicros,
        );
        const randomRoundUp = recommendationPricing.randomRoundUp(
          displayedPriceInMicros,
          row.snapshotCostInMicros,
        );
        return [
          {
            ...getQuickActionMetadata(PriceEditAction.EndInX9),
            disabled: endInX9 === null,
            onSelect: () => applyPricing(endInX9, PriceEditAction.EndInX9),
            valueLabel: priceValueLabel(endInX9),
          },
          {
            ...getQuickActionMetadata(PriceEditAction.RandomRoundUp),
            disabled: randomRoundUp === null,
            onSelect: () =>
              applyPricing(randomRoundUp, PriceEditAction.RandomRoundUp),
            valueLabel: priceValueLabel(randomRoundUp),
          },
          ...(bookmarkAction ? [bookmarkAction] : []),
          revertAction,
        ];
      }

      const addOnePercent = recommendationPricing.adjustGrossProfit(
        displayedGmPct,
        1,
        row.snapshotCostInMicros,
      );
      const subtractOnePercent = recommendationPricing.adjustGrossProfit(
        displayedGmPct,
        -1,
        row.snapshotCostInMicros,
      );
      return [
        {
          ...getQuickActionMetadata(GrossMarginEditAction.AddOnePercent),
          disabled: addOnePercent === null,
          onSelect: () =>
            applyPricing(addOnePercent, GrossMarginEditAction.AddOnePercent),
          valueLabel: gmValueLabel(addOnePercent),
        },
        {
          ...getQuickActionMetadata(GrossMarginEditAction.SubtractOnePercent),
          disabled: subtractOnePercent === null,
          onSelect: () =>
            applyPricing(
              subtractOnePercent,
              GrossMarginEditAction.SubtractOnePercent,
            ),
          valueLabel: gmValueLabel(subtractOnePercent),
        },
        ...(bookmarkAction ? [bookmarkAction] : []),
        revertAction,
      ];
    }, [
      applyPricing,
      bookmarkAction,
      clearOverride,
      currencyCode,
      displayedGmPct,
      displayedPriceInMicros,
      hasOverride,
      isPrice,
      row,
    ]);

    const subLabel = isPrice
      ? formatCurrency(row.snapshotPriceInMicros, currencyCode)
      : formatPercentage(row.snapshotGrossProfitInPercentage, 1, 1);

    const popoverProps = useMemo<PricingTableInputPopoverConfig>(
      () => ({
        currencyCode,
        historyContext: {
          enabled: isHistoricalPricingContextEnabled,
          field: inputType,
          history: row.invoiceHistory,
        },
        isAnchorSettled,
        kind: PricingTableInputPopoverKind.QuickActionsWithHistory,
      }),
      [
        currencyCode,
        inputType,
        isAnchorSettled,
        isHistoricalPricingContextEnabled,
        row.invoiceHistory,
      ],
    );

    const pricingTableInput = (
      <PricingTableInput
        bookmarked={isBookmarked}
        bookmarkNote={bookmarkNote}
        decimalPlaces={isPrice ? 2 : 1}
        disabled={disabled}
        inputID={inputID}
        onArrowNavigation={onArrowNavigation}
        onBlur={handleBlur}
        onFocus={reportCellFocus}
        onFocusChange={setIsFocused}
        onQuickActionsOpenChange={setQuickActionsOpen}
        originalValue={originalValue}
        popoverProps={popoverProps}
        quickActions={quickActions}
        quickActionsOpen={isQuickActionsOpen}
        updatedValue={updatedValue}
        validate={validate}
      />
    );

    return (
      <View collapsable={false} style={styles.container}>
        {isNoteOpen ? (
          <BookmarkNotePopover
            anchor={pricingTableInput}
            initialNote={bookmarkNote}
            onSave={onSaveNote}
          />
        ) : (
          pricingTableInput
        )}
        <View style={styles.snapshotValue}>
          <Typography
            color={theme.sk.color.text.tertiary}
            variant="paragraph-sm"
          >
            {subLabel}
          </Typography>
        </View>
      </View>
    );
  },
);

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'flex-end',
    gap: theme.sk.spacing.spaceXsmall,
  },
  snapshotValue: {
    // Match the input text's right offset: paddingRight (spaceSMedium) +
    // border (1px) so the snapshot value right-aligns with the input value.
    paddingRight: theme.sk.spacing.spaceSMedium + 1,
  },
}));
